/**
 * CreateShelfModal — create a personal custom shelf.
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button, cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useBookshelf } from '../context/BookshelfContext';
import { SHELF_EMOJIS } from '../constants';

const TONES = [
  { key: 'coral', className: 'bg-coral' },
  { key: 'yellow', className: 'bg-yellow' },
  { key: 'sage', className: 'bg-sage' },
  { key: 'blue', className: 'bg-blue' },
];

export default function CreateShelfModal({ isOpen, onClose, onCreated }) {
  const { shelves, createShelf } = useBookshelf();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [emoji, setEmoji] = useState('📚');
  const [tone, setTone] = useState('coral');
  const [error, setError] = useState('');

  const reset = () => {
    setName(''); setDescription(''); setEmoji('📚'); setTone('coral'); setError('');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return setError('Shelf name is required');
    if (trimmed.length > 50) return setError('Shelf name should be under 50 characters');
    if (shelves.some(s => s.name.toLowerCase() === trimmed.toLowerCase())) {
      return setError('You already have a shelf with this name. Try adding a number or emoji.');
    }

    try {
      const newShelf = await createShelf({ name: trimmed, description, emoji, tone, isPublic: true });
      toast(`Created “${trimmed}” shelf`);
      onCreated?.(newShelf);
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create shelf');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="w-[92vw] max-w-md rounded-2xl border-line bg-card p-6 text-ink">
        <DialogTitle className="font-display text-xl font-semibold text-ink">Create a New Shelf</DialogTitle>

        {error && <p className="mt-3 rounded-lg bg-coral/10 px-3 py-2 text-xs font-medium text-coral">{error}</p>}

        <div className="mt-4">
          <label className="text-xs font-semibold uppercase tracking-wide text-subtle">Shelf Name *</label>
          <Input
            value={name}
            onChange={e => { setName(e.target.value); setError(''); }}
            placeholder="e.g., Comfort Reads"
            className="mt-1.5 rounded-xl border-line bg-cream"
          />
        </div>

        <div className="mt-4">
          <label className="text-xs font-semibold uppercase tracking-wide text-subtle">Description (optional)</label>
          <Textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Books that feel like a warm blanket…"
            className="mt-1.5 rounded-xl border-line bg-cream text-sm"
          />
        </div>

        <div className="mt-4">
          <label className="text-xs font-semibold uppercase tracking-wide text-subtle">Icon</label>
          <div className="mt-1.5 grid grid-cols-8 gap-1.5">
            {SHELF_EMOJIS.map(e => (
              <button
                key={e}
                type="button"
                onClick={() => setEmoji(e)}
                className={cx(
                  'grid h-9 w-9 place-items-center rounded-lg border text-base transition-colors',
                  emoji === e ? 'border-coral bg-coral/10' : 'border-line hover:border-coral/40'
                )}
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs font-semibold uppercase tracking-wide text-subtle">Accent</label>
          <div className="mt-1.5 flex gap-2.5">
            {TONES.map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTone(t.key)}
                aria-label={`${t.key} accent`}
                className={cx('h-7 w-7 rounded-full ring-offset-2 ring-offset-card transition-all', t.className, tone === t.key ? 'ring-2 ring-ink' : 'opacity-70 hover:opacity-100')}
              />
            ))}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2.5">
          <Button variant="secondary" icon={false} onClick={handleClose}>Cancel</Button>
          <Button variant="primary" icon={false} onClick={handleSubmit}>Create Shelf</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
